import { useState, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import { useToast } from './useToast';
import logger from '../utils/logger';

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

export const useNotifications = () => {
    const { token: authToken } = useAuth();
    const { showToast } = useToast();
    const [expoPushToken, setExpoPushToken] = useState<string | undefined>('');
    const notificationListener = useRef<Notifications.Subscription>();
    const responseListener = useRef<Notifications.Subscription>();

    const registerForPushNotificationsAsync = async () => {
        let token;
        if (!Device.isDevice) {
            logger.warn('Must use physical device for Push Notifications');
            return;
        }

        const { status: existingStatus } = await Notifications.getPermissionsAsync();
        let finalStatus = existingStatus;
        if (existingStatus !== 'granted') {
            const { status } = await Notifications.requestPermissionsAsync();
            finalStatus = status;
        }
        if (finalStatus !== 'granted') {
            // Silently fail if user denies permission.
            // A settings screen could allow them to enable it later.
            logger.info('User denied push notification permissions');
            return;
        }
        
        try {
             token = (await Notifications.getExpoPushTokenAsync()).data;
        } catch (e) {
            logger.error('Failed to get push token', e);
            showToast('Error', 'Could not get push token for notifications.', 'error');
        }


        if (Platform.OS === 'android') {
            Notifications.setNotificationChannelAsync('default', {
                name: 'default',
                importance: Notifications.AndroidImportance.MAX,
                vibrationPattern: [0, 250, 250, 250],
                lightColor: '#FF231F7C',
            });
        }

        return token;
    };

    useEffect(() => {
        if (authToken) {
             registerForPushNotificationsAsync().then(token => {
                if (token) {
                    setExpoPushToken(token);
                    api.registerPushToken(token, authToken)
                        .then(() => logger.info('Push token registered successfully'))
                        .catch(err => logger.error('Could not register push token with backend', err));
                }
            });

            notificationListener.current = Notifications.addNotificationReceivedListener(notification => {
                logger.debug('Notification received while app is foregrounded', notification);
            });

            responseListener.current = Notifications.addNotificationResponseReceivedListener(response => {
                logger.info('User tapped on notification', response);
                // Here you could add navigation logic based on notification data
                // e.g., navigation.navigate('Profile');
            });

            return () => {
                if (notificationListener.current) {
                    Notifications.removeNotificationSubscription(notificationListener.current);
                }
                if (responseListener.current) {
                    Notifications.removeNotificationSubscription(responseListener.current);
                }
            };
        }
    }, [authToken]);

    return { expoPushToken };
};
